/**
 * PageContainer - the standard page shell.
 *
 * Twelve pages each opened with their own hand-written copy of the same
 * wrapper — `<div className="p-24px font-sans">` and near-misses of it. The
 * copies had already drifted: eight used a flat `p-24px`, three used the
 * responsive `p-16px tablet:p-24px`, and the flat ones spent 48px of a 375px
 * phone screen on padding. None of them carried the page's `data-ref` or BEM
 * block class, so `src/pages` was the one layer of the atomic hierarchy with
 * essentially no structural markers.
 *
 * This is that wrapper, once: responsive padding everywhere, the page's own
 * identity on the root element, and a `width` token for the two pages that
 * constrain their content.
 *
 * Pages that compose a richer template (JobListTemplate, RepeatsListTemplate)
 * don't need this — they pass their `dataRef` to that template instead.
 *
 * @example
 * <PageContainer block="work-order-detail-page" dataRef="page-work-order-detail-page">
 *     …
 * </PageContainer>
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * How wide the page's content may grow.
 *
 *   full    — fills the available width (list and detail pages)
 *   wide    — capped at 1280px and centred; stops dashboards and calendars
 *             from stretching to unreadable line lengths on a desktop monitor
 *   content — capped at 1024px and centred (the checklist editor)
 *   narrow  — capped at 3xl and centred, for read-heavy pages like the profile
 *   form    — capped at 720px, the comfortable single-column form measure
 */
export type PageContainerWidth = 'full' | 'wide' | 'content' | 'narrow' | 'form';

export interface PageContainerProps {
    children: React.ReactNode;
    /**
     * The composing page's atomic-design marker, e.g.
     * "page-work-order-detail-page". Required: the whole point of the
     * shell is that the rendered DOM names the real page.
     */
    dataRef: string;
    /** The page's BEM block class, e.g. "work-order-detail-page". */
    block?: string;
    /** Content width token. Defaults to 'full'. */
    width?: PageContainerWidth;
    /** Optional extra classes merged onto the root element. */
    className?: string;
}

/* ============================================
 * WIDTH TOKENS
 * ============================================ */

const WIDTH_CLASSES: Record<PageContainerWidth, string> = {
    full: '',
    wide: 'max-w-[1280px] mx-auto',
    content: 'max-w-[1024px] mx-auto',
    narrow: 'max-w-3xl mx-auto',
    form: 'max-w-[720px]',
};

/* ============================================
 * COMPONENT
 * ============================================ */

export const PageContainer: React.FC<PageContainerProps> = ({ children, dataRef, block, width = 'full', className }) => {
    return (
        /* p-16px on phones, p-24px from tablet up. The flat 24px the copies
           used cost a third of the usable width on a 375px handset, which is
           the screen most of this app's field use happens on. */
        <div
            className={[block ?? '', 'page-container', `page-container--${width}`, 'p-16px tablet:p-24px font-sans', WIDTH_CLASSES[width], className ?? '']
                .filter(Boolean)
                .join(' ')}
            data-ref={dataRef}
        >
            {children}
        </div>
    );
};

export default PageContainer;
